import { LangCode } from '@/components/i18n/lang-context'
import { SITE_IS_PROD } from '@/config/site.config'
import type { Category, PostsConnection } from '@/types/blog'
import request, { gql } from 'graphql-request'
import { getPostsHygraphQLApi } from '../services'
import { HttpError } from '../utils/http-error'
import { DEFAULT_HYGRAPH_LANG, toLocalesHygraph } from '../utils/utils'

const GRAPHQL_API = getPostsHygraphQLApi()

export const MAX_POSTS_PER_PAGE = process.env.NEXT_PUBLIC_MAX_POSTS_PER_PAGE ? Number(process.env.NEXT_PUBLIC_MAX_POSTS_PER_PAGE) : 10
export const MAX_FEATURED_POSTS = process.env.NEXT_PUBLIC_MAX_FEATURED_POSTS ? Number(process.env.NEXT_PUBLIC_MAX_FEATURED_POSTS) : 5; 
export const MAX_POSTS_PER_CATEGORY = process.env.NEXT_PUBLIC_MAX_POSTS_PER_CATEGORY ? Number(process.env.NEXT_PUBLIC_MAX_POSTS_PER_CATEGORY) : 20;

export const getCategories = async (nbOfPost: number = MAX_POSTS_PER_CATEGORY, locale: LangCode = DEFAULT_HYGRAPH_LANG) : Promise<Category[]> => {
  if(!GRAPHQL_API) return []
  const query = gql`
    query GetCategories($locales: [Locale!]!) {
      categoriesConnection (locales: $locales) {
        edges {
          node {
            id
            name
            slug
            posts(first: ${nbOfPost}${SITE_IS_PROD ? ', where: { production: true }' : ''}) {
              id
            }
          }
        }
      }
    }
  `
  return request(GRAPHQL_API, query, { locales: toLocalesHygraph(locale) }).then((result: any) => {
    return result.categoriesConnection.edges.map((edge: { node: any }) => edge.node)
  })
}

export const getPostDetails = async (slug: string, locale: LangCode = DEFAULT_HYGRAPH_LANG) => {
  if(!GRAPHQL_API) return undefined
  const query = gql`
    query GetPostDetails($slug: String!, $locales: [Locale!]!) {
      post (where: { slug: $slug }, locales: $locales) {
        id
        author {
          id
          slug
          name
          title
          email
          phone
          linkedInUserId
          linkedInShowcaseId
          linkedInCompanyId
          twitterId
          instagramId
          facebookId
          youtubeId
          bio
          photo { url width height alt }
        }
        firstPublishedAt
        publishedAt
        production
        slug
        title
        excerpt
        metaTitle
        metaDescription
        content {
          raw
          markdown
        }
        featuredImage { url width height alt }
        featuredPost
        categories { id name slug }
      }
    }
  `
  return request(GRAPHQL_API, query, { slug, locales: toLocalesHygraph(locale) }).then((value: any) => {
    const result = value.post
    if(SITE_IS_PROD && !result.production)
      throw new HttpError(404, 'Post with slug ID "' + slug + '" is not found among production posts')
    return result
  })
}

export const getPostsPaged = async ({
  page = 1,
  first = MAX_POSTS_PER_PAGE,
  locale = DEFAULT_HYGRAPH_LANG,
  search = '',
  categories = [],
  featured,
  orderBy = 'firstPublishedAt_DESC'
}: {
  page?: number
  first?: number
  locale?: LangCode
  search?: string
  categories?: string[]
  featured?: boolean
  orderBy?: ('firstPublishedAt_ASC'|'firstPublishedAt_DESC'|'publishedAt_ASC'|'publishedAt_DESC')
}): Promise<PostsConnection> => {

  if (!GRAPHQL_API) return { edges: [], pageInfo: { hasNextPage: false }, aggregate: { count: 0 } }

  const skip = (page - 1) * first

  // Build where conditions safely using GraphQL variables
  let whereConditions: any = {}

  if (SITE_IS_PROD) {
    whereConditions.production = true
  }

  if (search && search.trim()) {
    whereConditions.OR = [
      { title_contains: search.trim() },
      { excerpt_contains: search.trim() }
    ]
  }

  if (categories.length > 0) {
    whereConditions.OR = [
      { categories_some: { id_in: categories } },
      { categories_some: { slug_in: categories } }
    ]
  }
  if (featured !== undefined) {
    whereConditions.featuredPost = featured
  }

  const hasWhereConditions = Object.keys(whereConditions).length > 0

  // Helper function to build the query dynamically
  const buildPostsQuery = (withWhere: boolean) => {
    const whereParam = withWhere ? '$where: PostWhereInput, ' : ''
    const whereClause = withWhere ? 'where: $where,' : ''

    return gql`
      query GetPostsPaged($locales: [Locale!]!, $first: Int!, $skip: Int!, ${whereParam}) {
        postsConnection(
          orderBy: ${orderBy}
          ${whereClause}
          locales: $locales
          first: $first
          skip: $skip
        ) {
          aggregate { count }
          pageInfo { hasNextPage }
          edges {
            node {
              id
              author {
                id
                slug
                name
                title
                email
                phone
                linkedInUserId
                linkedInShowcaseId
                linkedInCompanyId
                twitterId
                instagramId
                facebookId
                youtubeId
                bio
                photo { url width height alt }
              }
              slug
              title
              excerpt
              firstPublishedAt
              publishedAt
              featuredPost
              featuredImage { url width height alt }
              categories { id name slug }
            }
          }
        }
      }
    `
  }

  const query = buildPostsQuery(hasWhereConditions)

  const variables: any = {
    locales: toLocalesHygraph(locale),
    first,
    skip
  }

  if (hasWhereConditions) {
    variables.where = whereConditions
  }

  return request<{ postsConnection: PostsConnection }>(GRAPHQL_API, query, variables).then(res => res.postsConnection)
}